import { useEffect, useReducer, useRef, useState } from "react";
import { glbVersionOfUrl, useAssetVariants } from "../../../lib/kitVariant";
import { hubColor } from "../../../lib/hubOutput";
import { useParams } from "react-router-dom";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { useCourseApi } from "../../../lib/api/courseApi";
import { useStageModels } from "../../stage";
import { usePaletteSet } from "../../../hooks/usePaletteSet";
import { FeBanner } from "../../../components/FeBanner";
import { Loading, ScreenRoot } from "../../../components/Shell";
import { ZoomControls } from "../../../components/ZoomControls";
import { RotateControls, useViewTurn } from "../../../components/ViewTurn";
import { useTopBar } from "../../../components/TopBar";
import type { TopBarChip } from "../../../components/TopBar";
import type { GalleryViewLike, GalleryViewRequest } from "../../course";
import { ROOM_SCENE } from "../../course";
import {
  PrincipleButton,
  PrincipleLabelLayer,
  PrincipleLegend,
  usePrincipleLabels,
} from "../../course";
import { hasMessage, useT } from "../../../i18n";
import {
  arenaPlayers,
  densityFocus,
  HIGHLIGHT_BACK,
  machinePos,
  ownerOf,
  RELAY_HANDOFF_MS,
  relayFocus,
  sectionsOf,
  turnFocus,
} from "../../room";
import type { Focus } from "../../room";
import type { RoomConnection } from "../../room";
import { courseTone, useConsoleApi } from "../../room";
import type { ConsoleApi } from "../../room";
import { roomDeps } from "../../room";
import { mmss } from "../../room";
import { useBoardConnection } from "../../room";
import { RelayCells, RelayNowLine, useRelayView } from "../../room";
import type { RelayView } from "../../room";
import "../../../styles/course.css";
import "../../../styles/board.css";
import type { BoardCourses } from "../types";

/** 자동 카메라가 목표를 다시 정하는 간격(흔들리지 않게 — 사이는 렌더러가 부드럽게 따라간다) */
export const AUTO_CAMERA_MS = 1000;
/** 소리(beep) 표시를 켜 두는 시간 */
export const BEEP_SHOW_MS = 1200;
/** 이름 라벨 크기(배율) — A−/A+ 한 번에 0.25 */
export const LABEL_SCALES = [0.75, 1, 1.25, 1.5, 1.75, 2, 2.5] as const;
const SCALE_KEY = "pf.board.labelScale";

/** 교류 룸 반 구분을 다시 읽는 간격(명부는 자주 바뀌지 않는다) */
export const BOARD_COURSE_POLL_MS = 5000;

/** 교류 룸이면 반 구분(콘솔 응답의 courses · 학생 courseId) — 반 룸이면 null */
function useBoardCourses(api: ConsoleApi | null): BoardCourses | null {
  const [v, setV] = useState<BoardCourses | null>(null);
  useEffect(() => {
    if (!api) return;
    let alive = true;
    const read = () =>
      api
        .console()
        .then((c) => {
          if (!alive) return;
          if (c.kind !== "exchange" || c.courses.length < 2) return setV(null);
          const tone = new Map<number, "a" | "b">();
          for (const s of c.students) {
            const k = courseTone(c.courses, s.courseId);
            if (s.machine !== null && k) tone.set(s.machine, k);
          }
          setV({ courses: c.courses, tone });
        })
        .catch(() => {});
    void read();
    const h = window.setInterval(() => void read(), BOARD_COURSE_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(h);
    };
  }, [api]);
  return v;
}

/** 허브 빛 이름 → 색 — 셸 로컬 HUD 와 같은 표(lib/hubOutput) */
export { hubColor };

function loadScale(): number {
  try {
    const v = Number(window.localStorage.getItem(SCALE_KEY));
    return (LABEL_SCALES as readonly number[]).includes(v) ? v : 1;
  } catch {
    return 1;
  }
}
function saveScale(v: number) {
  try {
    window.localStorage.setItem(SCALE_KEY, String(v));
  } catch {
    /* 사생활 보호 모드 — 이 창에서만 */
  }
}

/**
 * FS-12-01 칠판 뷰(SCR v0.6 sfs-12 · SR-TCH-02 · SR-AUTH-04 · SR-SOC-04) — /board/:roomId(교사 · 새 창 · 교실 앞 대형 화면).
 *  - 렌더 전용: board 조인 토큰으로 붙고 입력 채널이 없다(교사 콘솔이 제어 — ★ 하이라이트 · 일시정지 · 모드 · 스크립트)
 *  - 조감 · 자동 카메라: 움직이는 기계가 많이 모인 곳(1초마다 목표) · ★ 하이라이트면 그 기계를 따라간다
 *  - 상단: 📺 반 · 모드 · (스크립트 단계) · 남은 시간(크게) · 자동 카메라/하이라이트
 *  - 장면 위: 밀어내기 '지금: A vs B'만(승패·점수·순위 없음) · ⛔ 막힌 포트 라벨(+ 설명 한 줄 — 승패 아님)
 *    · 이름 크기 A−/A+(이 창에만 기억) · 일시정지 표시 · 허브 빛(라벨 옆 색 점)·소리(🔊 잠깐)
 *  - 1080p · 4K: 글자는 화면 폭에 비례(clamp) — 라벨 배율과 곱한다
 *  - P3-S4 교류 룸(SCR v0.7 검토 §4 FS-12-01 변형): 두 반이 각자 칠판을 띄운다. 반 구분은 **이름 라벨 색 테두리만**
 *    (호스트 반 a · 상대 반 b — 두 칠판에서 같은 색) + 색 풀이(반 이름). 반 점수·반 대항 표시 없음(DR-01)
 *  - 💡 원리 보기(SR-PHY-07 · 2026-09-28): 칠판 오른쪽 위 토글 — 보는 기계는 **교사 콘솔 ★ 하이라이트**(릴레이면 지금 구간
 *    기계). 룸 서버에 `pr {on, machine}` 을 보내고 받은 `pp`(서버 권위 월드의 읽기 전용 값)를 학생 화면과 같은 오버레이로 그린다
 *    (SU — 수치 라벨 · 범례). 고른 기계가 없으면 안내만. 입력이 아니다(해시 · 리플레이 무관).
 *  - 확대 · 축소 · 회전(2026-10-10 요청): 오른쪽 가운데 ＋ · －(휠) · ↺ · ↻(45°) · 장면 좌우 끌기 · ⟲ 처음 방향.
 *    자동 카메라 · ★ 하이라이트가 고르는 점은 그대로 — 배율(setZoom)과 보는 방향(heading)만 바꾼다. 이 창에만(저장 안 함).
 */
export function BoardScreen() {
  const t = useT();
  const { roomId = "" } = useParams();
  const api = useConsoleApi();
  const conn = useBoardConnection(api);
  const courses = useBoardCourses(api);
  const field = useFieldApi();
  const [course, setCourse] = useState<string | null>(null);
  const [scale, setScale] = useState(loadScale);
  const [, tick] = useReducer((x: number) => x + 1, 0);

  useEffect(() => {
    if (!field) return;
    let alive = true;
    field
      .teacherOverview()
      .then((d) => alive && setCourse(d?.courseLabel ?? null))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [field]);

  // 남은 시간 · 스크립트 · 소리 표시 — 0.5초마다 다시 그린다
  useEffect(() => {
    const h = window.setInterval(tick, 500);
    return () => window.clearInterval(h);
  }, []);

  const state = conn?.state ?? { kind: "connecting" as const };
  const meta = conn?.meta ?? null;
  // 작품마다 제 appearance.palette 이름의 팔레트(2026-09-30 팔레트 버전 전환)
  const palette = usePaletteSet(
    field,
    meta?.machines.map((m) => m.appearance),
  );
  const modeId = meta?.mode.id ?? "";
  const modeKey = modeId === "gallery" || !modeId ? "lobby" : modeId;
  const modeName = hasMessage(`teacher.room.modeName.${modeKey}`)
    ? t(`teacher.room.modeName.${modeKey}`)
    : modeKey;
  const timerLeft = conn?.timerLeftS() ?? null;
  const script = conn?.script && !conn.script.done ? conn.script : null;
  const scriptLeft = conn?.scriptLeftS() ?? null;
  const left = timerLeft ?? scriptLeft;
  const hl = conn?.highlight ?? null;
  const hlName = hl !== null ? (conn?.members.find((m) => m.machine === hl)?.name ?? "") : null;
  const objects =
    meta?.mode.kind === "sandbox" && Array.isArray(meta.mode.objects)
      ? meta.mode.objects.length
      : null;
  // P3-S2 FS-12-01 릴레이 변형(v07-15) — 진행 막대 · 지금 구간 자동 카메라 · 별명 없음
  const isRelay = meta?.mode.kind === "relay";
  const relay = useRelayView(isRelay ? (conn ?? null) : null, isRelay ? meta : null);

  const chips: TopBarChip[] = [];
  if (script) {
    const s = script.steps[script.index]!;
    chips.push({
      label: t("board.step", {
        icon: s.icon,
        name: t(`script.step.${s.key}`),
        n: script.index + 1,
        total: script.steps.length,
      }),
      testId: "board-step",
    });
  }
  if (objects !== null)
    chips.push({
      label: t("room.sandbox.objects", { n: objects, max: 50 }),
      testId: "board-objects",
    });
  if (isRelay && relay.view.phase === "running" && relay.view.stages.length)
    chips.push({
      label: t("board.relay.chip", {
        n: (relay.view.current ?? 0) + 1,
        total: relay.view.stages.length,
      }),
      testId: "board-relay-chip",
      big: true,
    });
  if (left !== null)
    chips.push({ label: t("board.left", { left: mmss(left) }), testId: "board-left", big: true });
  if (meta)
    chips.push({
      label: hlName !== null ? t("board.highlight", { name: hlName }) : t("board.auto"),
      testId: "board-camera",
    });
  useTopBar({
    icon: "📺",
    title: meta
      ? t("board.title", { course: course ?? t("board.room"), mode: modeName })
      : t("board.screenTitle"),
    chips,
  });

  const bump = (dir: 1 | -1) => {
    const i = LABEL_SCALES.indexOf(scale as (typeof LABEL_SCALES)[number]);
    const next = LABEL_SCALES[Math.max(0, Math.min(LABEL_SCALES.length - 1, i + dir))]!;
    setScale(next);
    saveScale(next);
  };

  return (
    <ScreenRoot id="FS-12-01" title={t("board.screenTitle")} hideTitle fill className="pf-board">
      {state.kind === "failed" ? (
        <FeBanner code="FE-303" audience="teacher" actions={[]} />
      ) : state.kind === "rejected" ? (
        <FeBanner code={state.code} audience="teacher" actions={[]} />
      ) : state.kind === "closed" || state.kind === "replaced" ? (
        <section className="pf-pane pf-pane--hero pf-board__notice" data-testid="board-closed">
          <h2 className="pf-h3">{t("board.closed")}</h2>
          <p className="pf-muted">{t("board.closedNext")}</p>
        </section>
      ) : !meta || !conn ? (
        <Loading label={t("board.connecting", { room: roomId })} />
      ) : (
        <BoardScene
          conn={conn}
          relay={isRelay ? relay : null}
          courses={courses}
          palette={palette}
          scale={scale}
          onScale={bump}
          reconnecting={state.kind === "reconnecting"}
        />
      )}
    </ScreenRoot>
  );
}

function BoardScene({
  conn,
  relay,
  courses,
  palette,
  scale,
  onScale,
  reconnecting,
}: {
  conn: RoomConnection;
  /** 교류 룸 반 구분(라벨 색 테두리) — 반 룸이면 null */
  courses: BoardCourses | null;
  /** 릴레이 모드면 지금 모습(별명 없음) */
  relay: { view: RelayView; skipShown: number | null } | null;
  palette: PaletteLike | null;
  scale: number;
  onScale: (dir: 1 | -1) => void;
  reconnecting: boolean;
}) {
  const t = useT();
  const meta = conn.meta!;
  // 기계마다 교구 판 — 스파이크 프라임 판이면 허브 빛 · 소리 이름도 그 교구로(2026-11-03)
  const variants = useAssetVariants();
  const isPrime = (glbUrl: string | null | undefined) => variants.get(glbVersionOfUrl(glbUrl) ?? "") === "prime";
  // 자유 놀이터 교사 꾸밈의 올린 3D 모델(카탈로그 stage_model)
  const stageModels = useStageModels(useCourseApi());
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const labelRefs = useRef(new Map<number, HTMLSpanElement>());
  const [view, setView] = useState<"loading" | "3d" | "plan">("loading");
  const [vs, setVs] = useState<string | null>(null);
  const hlRef = useRef<number | null>(conn.highlight);
  hlRef.current = conn.highlight;
  const online = new Set(
    conn.members.filter((m) => m.online && m.machine !== null).map((m) => m.machine!),
  );
  const onlineRef = useRef(online);
  onlineRef.current = online;
  // 릴레이 — 지금 구간 기계(카메라) · 넘겨받기(ev done) 순간 확대
  const rv = relay?.view ?? null;
  const relayMachine =
    rv && rv.phase === "running" && rv.current !== null
      ? (rv.info[rv.current]?.machine ?? null)
      : null;
  const relayRef = useRef<{
    stage: number | null;
    machine: number | null;
    on: boolean;
    statuses: readonly string[];
  }>({ stage: null, machine: null, on: false, statuses: [] });
  relayRef.current = {
    stage: rv && rv.phase === "running" ? rv.current : null,
    machine: relayMachine,
    on: !!relay,
    statuses: rv?.stages ?? [],
  };
  const rvRef = useRef(rv);
  rvRef.current = rv;
  // 회전 — 고른 방향을 그리는 방향이 매 프레임 따라간다(공용 useViewTurn)
  const view3 = useViewTurn();
  const turnStep = view3.step;
  // 💡 원리 보기 — ★ 하이라이트 기계(릴레이면 지금 구간 기계) · 칠판은 교사 화면이라 수치 라벨(SU)
  const [principleOn, setPrincipleOn] = useState(false);
  const pTarget = conn.highlight ?? relayMachine;
  const pShow = principleOn && pTarget !== null;
  const labels = usePrincipleLabels();
  const viewRef = useRef<GalleryViewLike | null>(null);
  const principleRef = useRef<{ on: boolean; numbers: boolean; owners: string[] | null }>({
    on: false,
    numbers: true,
    owners: null,
  });
  principleRef.current = {
    on: pShow,
    numbers: true,
    owners: pTarget !== null ? [ownerOf(pTarget)] : null,
  };
  // 켠 적이 없으면 아무것도 보내지 않는다(렌더 전용 칠판 — 끄기만 보낼 일도 없다)
  const pSent = useRef(false);
  useEffect(() => {
    if (!pShow && !pSent.current) return;
    pSent.current = pShow;
    conn.sendPrinciple(pShow, pShow ? pTarget : null);
  }, [conn, pShow, pTarget]);
  useEffect(() => {
    viewRef.current?.setPrinciple?.(principleRef.current);
    if (!pShow) labels.place(null);
    // labels 는 ref 묶음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pShow, pTarget]);
  const handoff = useRef<{ machine: number | null; until: number; from: number } | null>(null);
  const [handoffShown, setHandoffShown] = useState<number | null>(null);
  useEffect(() => {
    if (!relay) return;
    return conn.onEvent((e) => {
      const ev = e.t === "relayStage" ? (e.ev as Record<string, unknown> | undefined) : undefined;
      if (ev?.kind !== "done" || typeof ev.stage !== "number") return;
      const st = ev.stage;
      const machine = rvRef.current?.info[st]?.machine ?? null;
      handoff.current = { machine, from: st, until: roomDeps().now() + RELAY_HANDOFF_MS };
      setHandoffShown(st);
      window.setTimeout(() => setHandoffShown((x) => (x === st ? null : x)), RELAY_HANDOFF_MS);
    });
  }, [conn, relay === null]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let alive = true;
    let handle: GalleryViewLike | null = null;
    let offPaint: (() => void) | null = null;
    let raf = 0;
    // 자동 카메라 목표 — 1초마다(앞 프레임 사본과 비교)
    let prev: { at: number; t: Float32Array } | null = null;
    let target: Focus = { x: 0, z: 4, back: 12 };
    const sections = sectionsOf(meta);
    let tinted = "";
    const req: GalleryViewRequest = {
      bodies: meta.bodies as unknown as GalleryViewRequest["bodies"],
      exhibits: meta.machines.map((m) => ({
        owner: m.owner,
        glbUrl: m.glbUrl,
        appearance: m.appearance as GalleryViewRequest["exhibits"][number]["appearance"],
        palette,
      })),
      quality: "high",
      roomScene: ROOM_SCENE,
      // 자유 놀이터 교사 꾸밈(보기 전용 · meta.mode.decor)
      ...(meta.mode.kind === "sandbox" && meta.mode.decor
        ? { roomDecor: meta.mode.decor, stageModelUrl: stageModels.url ?? undefined }
        : {}),
    };
    roomDeps()
      .createGalleryView(canvas, req)
      .then(async (h) => {
        handle = h;
        if (!alive) return h.dispose();
        await h.ready;
        if (!alive) return;
        h.setActive(meta.machines.map((m) => m.owner));
        viewRef.current = h;
        h.setPrinciple?.(principleRef.current);
        offPaint = h.onPaint(() => {
          if (principleRef.current.on) labels.place(h);
        });
        const draw = () => {
          const f = conn.frame();
          const turnBy = turnStep(roomDeps().now());
          if (f) {
            h.update(f);
            const now = roomDeps().now();
            if (!prev || now - prev.at >= AUTO_CAMERA_MS) {
              target = densityFocus(
                meta,
                prev?.t ?? null,
                f.transforms,
                prev ? (now - prev.at) / 1000 : 0,
                onlineRef.current,
              );
              prev = { at: now, t: f.transforms.slice() };
            }
            const hl = hlRef.current;
            const p = hl !== null ? machinePos(meta, f.transforms, hl) : null;
            // 릴레이 — ★ 하이라이트가 없으면 지금 구간(넘겨받기 직후 잠깐은 끝난 구간을 가까이)
            const r = relayRef.current;
            const ho = handoff.current && handoff.current.until > now ? handoff.current : null;
            const rf =
              !p && r.on
                ? relayFocus(
                    meta,
                    sections,
                    f.transforms,
                    ho ? ho.from : r.stage,
                    ho ? ho.machine : r.machine,
                    !!ho,
                  )
                : null;
            // 구간 상태 색(나감 존 · 벽 — 다시 준비는 중립색 · DR-01) — 바뀔 때만
            const key = r.on ? r.statuses.join(",") : "";
            if (key !== tinted) {
              tinted = key;
              h.setSectionStatus?.(r.on ? r.statuses : []);
            }
            const v = p ? { ...p, back: HIGHLIGHT_BACK } : (rf ?? target);
            // 회전 — 바라보는 점은 그대로 두고 방향만(turnFocus)
            const tv = turnFocus(v, turnBy);
            h.setViewpoint(
              { x: tv.x, z: tv.z },
              { ...(v.back ? { back: v.back } : {}), heading: tv.heading ?? 0 },
            );
            for (const m of meta.machines) {
              const el = labelRefs.current.get(m.machine);
              const q = h.project?.(m.bodyStart, 0.3);
              if (el) {
                el.style.display = q ? "" : "none";
                if (q) el.style.transform = `translate(${q.x}px, ${q.y}px)`;
              }
            }
          }
          raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        setView("3d");
      })
      .catch(() => alive && setView("plan"));
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      offPaint?.();
      viewRef.current = null;
      handle?.dispose();
    };
    // FullState(모드 전환 · 배치 · 재접속)마다 새로 싣는다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, palette, stageModels.url]);

  // 밀어내기 — 지금 경기(링 안 둘 · 0.5초마다)
  useEffect(() => {
    if (meta.mode.kind !== "sumo") {
      setVs(null);
      return;
    }
    const read = () => {
      const f = conn.frame();
      const names = new Map(conn.members.map((m) => [m.machine, m.name]));
      const live = f ? arenaPlayers(meta, f.transforms) : [];
      setVs(
        live.length
          ? live.map((x) => x.machines.map((m) => names.get(m) ?? "").join(" vs ")).join(" · ")
          : null,
      );
    };
    read();
    const h = window.setInterval(read, 500);
    return () => window.clearInterval(h);
  }, [conn, meta]);

  const now = roomDeps().now();
  const names = new Map(conn.members.filter((m) => m.machine !== null).map((m) => [m.machine!, m]));
  const anyStall = [...conn.stalls.values()].some((p) => p.length);
  const anyHub = conn.hubLight.size > 0 || conn.hubBeepAt.size > 0;
  const paused = conn.paused;

  return (
    <div
      className="pf-board__scene"
      data-testid="board-scene"
      data-view={view}
      data-highlight={conn.highlight ?? undefined}
      data-principle={pShow ? "su" : "off"}
      style={{ ["--pf-board-label" as string]: String(scale) }}
    >
      <canvas
        ref={canvasRef}
        className="pf-board__canvas"
        aria-hidden="true"
        data-testid="board-canvas"
        {...view3.canvasHandlers}
      />
      <PrincipleLabelLayer layer={labels.layer} on={pShow && view === "3d"} />
      {meta.machines.map((m) => {
        const who = names.get(m.machine);
        // 릴레이 칠판은 별명 없음(v07-15 · DR-01 — 다시 준비 구간을 이름으로 가리키지 않는다)
        if (!who || relay) return null;
        const ports = conn.stalls.get(m.machine) ?? [];
        const light = conn.hubLight.get(m.machine);
        const beep = (conn.hubBeepAt.get(m.machine) ?? -Infinity) > now - BEEP_SHOW_MS;
        return (
          <span
            key={m.owner}
            className="pf-board__label"
            data-machine={m.machine}
            data-online={who.online}
            data-highlight={conn.highlight === m.machine ? "true" : undefined}
            data-stalled={ports.length ? "true" : undefined}
            data-tone={courses?.tone.get(m.machine)}
            ref={(el) => {
              if (el) labelRefs.current.set(m.machine, el);
              else labelRefs.current.delete(m.machine);
            }}
          >
            {light && (
              <i
                className="pf-board__light"
                style={{ background: hubColor(light) }}
                data-testid="board-light"
                aria-label={t(isPrime(m.glbUrl) ? "board.lightOfPrime" : "board.lightOf", { color: light })}
              />
            )}
            {who.name}
            {ports.length > 0 && (
              <span className="pf-board__stall" data-testid="board-stall">
                {t("board.stall", { ports: ports.join("·") })}
              </span>
            )}
            {beep && (
              <span
                className="pf-board__beep"
                data-testid="board-beep"
                aria-label={t(isPrime(m.glbUrl) ? "board.beepPrime" : "board.beep")}
              >
                🔊
              </span>
            )}
          </span>
        );
      })}
      <div className="pf-board__hud pf-board__hud--tl">
        {courses && !relay && (
          // 색 풀이(반 이름만 — 점수·반 대항 없음)
          <p className="pf-board__chip pf-board__legend" data-testid="board-course-legend">
            {courses.courses.map((c, i) => (
              <span key={c.courseId} className="pf-board__legend-item">
                <i
                  className="pf-board__swatch"
                  data-tone={i === 0 ? "a" : "b"}
                  aria-hidden="true"
                />
                {c.label}
              </span>
            ))}
          </p>
        )}
        {relay && rv && (
          <div className="pf-board__relay" data-testid="board-relay">
            <RelayNowLine
              view={rv}
              skipShown={relay.skipShown}
              testId="board-relay-now"
              className="pf-board__chip pf-board__chip--big"
            />
            <RelayCells
              view={rv}
              mine={false}
              label={t("board.relay.barLabel", { total: rv.stages.length })}
              testId="board-relay-bar"
            />
            {handoffShown !== null && handoffShown + 1 < rv.stages.length && (
              <p className="pf-board__chip" data-testid="board-relay-handoff">
                {t("board.relay.handoff", { from: handoffShown + 1, to: handoffShown + 2 })}
              </p>
            )}
          </div>
        )}
        {vs && (
          <p className="pf-board__chip pf-board__chip--big" data-testid="board-vs">
            {t("board.vs", { vs })}
          </p>
        )}
        {anyStall && !relay && (
          <p className="pf-board__chip pf-board__chip--err" data-testid="board-stall-legend">
            {t("board.stallLegend")}
          </p>
        )}
      </div>
      <div
        className="pf-board__hud pf-board__hud--tr pf-board__chip"
        role="group"
        aria-label={t("board.labelSize")}
        data-testid="board-label-size"
      >
        <span aria-hidden="true">{t("board.labelSize")}</span>
        <button
          type="button"
          className="pf-board__size"
          aria-label={t("board.smaller")}
          disabled={scale <= LABEL_SCALES[0]}
          onClick={() => onScale(-1)}
        >
          A⁻
        </button>
        <button
          type="button"
          className="pf-board__size"
          aria-label={t("board.bigger")}
          disabled={scale >= LABEL_SCALES[LABEL_SCALES.length - 1]!}
          onClick={() => onScale(1)}
        >
          A⁺
        </button>
      </div>
      {view === "3d" && (
        <>
          <ZoomControls getView={() => viewRef.current} wheelTarget={canvasRef} />
          <RotateControls turn={view3} testId="board-rotate" />
        </>
      )}
      <div className="pf-board__hud pf-board__hud--principle" data-testid="board-principle">
        <PrincipleButton hud on={principleOn} onToggle={() => setPrincipleOn((o) => !o)} />
        {principleOn &&
          (pTarget === null ? (
            <p className="pf-board__chip" data-testid="board-principle-none">
              {t("board.principleNone")}
            </p>
          ) : (
            <>
              {/* 릴레이 칠판은 별명 없음(v07-15) — 그때는 기계 이름을 적지 않는다 */}
              {!relay && (
                <p className="pf-board__chip" data-testid="board-principle-of">
                  {t("board.principleOf", { name: names.get(pTarget)?.name ?? "" })}
                </p>
              )}
              <PrincipleLegend se={false} />
            </>
          ))}
      </div>
      {relay && rv?.phase === "allDone" && (
        <div className="pf-board__done" role="status" data-testid="board-relay-done">
          <p className="pf-relay-me__party" aria-hidden="true">
            🎉🔗🎉
          </p>
          <p>{t("board.relay.doneTitle")}</p>
          <p className="pf-board__chip">{t("board.relay.doneText", { total: rv.stages.length })}</p>
          <p className="pf-relay-me__badge">{t("board.relay.badge")}</p>
        </div>
      )}
      {(paused || reconnecting) && (
        <p
          className="pf-board__hud pf-board__hud--bl pf-board__chip"
          role="status"
          data-testid="board-paused"
        >
          {t(paused ? "board.paused" : "board.reconnecting")}
        </p>
      )}
      {anyHub && (
        <p
          className="pf-board__hud pf-board__hud--br pf-board__chip"
          data-testid="board-hub-legend"
        >
          {t(meta.machines.length > 0 && meta.machines.every((x) => isPrime(x.glbUrl)) ? "board.hubLegendPrime" : "board.hubLegend")}
        </p>
      )}
    </div>
  );
}
